import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { formatKRW, formatDateKo, formatDateTimeKo } from '@/lib/format';
import { useShamanContext, hasEverPaid } from './useShamanContext';
import { useSession } from '@/features/auth/useSession';
import { ko } from '@/i18n/ko';
import { requestBillingAuth, customerKeyFor } from '@/lib/toss';
import { env } from '@/lib/env';

interface SubscriptionSettings {
  monthly_price_krw: number;
  grace_days: number;
}

interface BillingKey {
  card_last4: string | null;
  card_company: string | null;
  issued_at: string;
}

interface SubPayment {
  id: string;
  amount_krw: number;
  status: string;
  attempted_at: string;
  paid_at: string | null;
  failure_reason: string | null;
}

export function ShamanBillingPage() {
  const { data: shamanCtx, refetch: refetchShaman } = useShamanContext();
  const { session } = useSession();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [actionBusy, setActionBusy] = useState(false);
  const [serverErr, setServerErr] = useState<string | null>(null);

  const { data: settings } = useQuery({
    queryKey: ['subscription-settings'],
    queryFn: async (): Promise<SubscriptionSettings | null> => {
      const { data, error } = await supabase
        .from('subscription_settings')
        .select('monthly_price_krw, grace_days')
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const shamanId = shamanCtx?.shaman.id;

  const { data: billingKey } = useQuery({
    queryKey: ['billing-key', shamanId],
    enabled: !!shamanId,
    queryFn: async (): Promise<BillingKey | null> => {
      const { data, error } = await supabase
        .from('billing_keys')
        .select('card_last4, card_company, issued_at')
        .eq('shaman_id', shamanId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: payments } = useQuery({
    queryKey: ['subscription-payments', shamanCtx?.subscription],
    enabled: !!shamanCtx?.shaman.id,
    queryFn: async (): Promise<SubPayment[]> => {
      if (!shamanCtx) return [];
      const { data: sub } = await supabase
        .from('subscriptions')
        .select('id')
        .eq('shaman_id', shamanCtx.shaman.id)
        .maybeSingle();
      if (!sub) return [];
      const { data, error } = await supabase
        .from('subscription_payments')
        .select('id, amount_krw, status, attempted_at, paid_at, failure_reason')
        .eq('subscription_id', sub.id)
        .order('attempted_at', { ascending: false })
        .limit(12);
      if (error) throw error;
      return data ?? [];
    },
  });

  // 토스 callback 처리: ?authKey=...&customerKey=...
  const registerMutation = useMutation({
    mutationFn: async (vars: { authKey: string; customerKey: string }) => {
      const { data, error } = await supabase.functions.invoke('register-billing-key', {
        body: vars,
      });
      if (error) throw new Error(error.message ?? '결제 등록 실패');
      if ((data as { error?: string })?.error) {
        throw new Error((data as { error: string; reason?: string }).reason ?? (data as { error: string }).error);
      }
      return data;
    },
    onSuccess: async () => {
      setSearchParams({});
      await Promise.all([
        refetchShaman(),
        qc.invalidateQueries({ queryKey: ['billing-key'] }),
        qc.invalidateQueries({ queryKey: ['subscription-payments'] }),
      ]);
    },
    onError: (e: Error) => setServerErr(e.message),
  });

  // URL에 토스 콜백 파라미터가 있으면 자동 처리
  const authKey = searchParams.get('authKey');
  const customerKey = searchParams.get('customerKey');
  if (authKey && customerKey && !registerMutation.isPending && registerMutation.status === 'idle') {
    registerMutation.mutate({ authKey, customerKey });
  }

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('cancel-subscription', { body: {} });
      if (error) throw new Error(error.message ?? '해지 실패');
      return data;
    },
    onSuccess: () => refetchShaman(),
  });

  async function handleRegisterCard() {
    if (!shamanCtx || !session?.user) return;
    setServerErr(null);
    setActionBusy(true);
    try {
      const baseUrl = env.APP_ORIGIN;
      await requestBillingAuth({
        customerKey: customerKeyFor(shamanCtx.shaman.id),
        customerEmail: session.user.email ?? `${shamanCtx.shaman.slug}@example.com`,
        customerName: shamanCtx.shaman.display_name_ko,
        successUrl: `${baseUrl}/me/billing`,
        failUrl: `${baseUrl}/me/billing?billingFail=1`,
      });
      // 위 호출은 페이지 전환이므로 여기 도달 X
    } catch (e) {
      setServerErr((e as Error).message);
    } finally {
      setActionBusy(false);
    }
  }

  const status = shamanCtx?.subscription?.status;
  const everPaid = hasEverPaid(shamanCtx?.subscription ?? null);
  const fail = searchParams.get('billingFail');

  return (
    <div className="space-y-4">
      <div className="card space-y-3">
        <h2 className="text-xl font-bold">월 구독</h2>
        <div className="rounded-lg bg-surface-alt p-4 space-y-1">
          <div className="text-base text-ink-muted">월 구독료</div>
          <div className="text-3xl font-bold">
            {settings ? formatKRW(settings.monthly_price_krw) : '—'}
          </div>
          {settings && (
            <div className="text-base text-ink-muted">
              결제 실패 시 {settings.grace_days}일 유예 후 자동 일시중지됩니다.
            </div>
          )}
        </div>

        {status === 'active' && (
          <div className="text-base">
            ✅ 구독 중 · 다음 결제일:{' '}
            {shamanCtx?.subscription?.next_charge_at
              ? formatDateKo(shamanCtx.subscription.next_charge_at, 'yyyy.MM.dd')
              : '—'}
          </div>
        )}
        {status === 'past_due' && (
          everPaid ? (
            <div className="text-base text-danger">⚠ 결제 실패 — 새 카드를 등록해주세요.</div>
          ) : (
            <div className="text-base text-warning">결제 수단을 등록하면 예약 페이지가 공개됩니다.</div>
          )
        )}
        {status === 'paused' && (
          <div className="text-base text-danger">⛔ 일시중지 — 카드 재등록 후 자동 재개됩니다.</div>
        )}
        {status === 'canceled' && (
          <div className="text-base text-ink-muted">구독이 해지되었습니다.</div>
        )}

        {fail && (
          <div className="text-base text-danger">결제 등록이 취소되거나 실패했어요. 다시 시도해주세요.</div>
        )}
        {serverErr && <div className="text-base text-danger">{serverErr}</div>}
        {registerMutation.isPending && (
          <div className="text-base text-ink-muted">결제 등록 중…</div>
        )}

        {billingKey ? (
          <div className="rounded-lg border border-ink-line p-4 space-y-1">
            <div className="text-base text-ink-muted">현재 등록된 카드</div>
            <div className="font-semibold">
              {billingKey.card_company ?? '카드'} •••• {billingKey.card_last4 ?? '----'}
            </div>
            <div className="text-base text-ink-muted">
              등록: {formatDateKo(billingKey.issued_at, 'yyyy.MM.dd')}
            </div>
          </div>
        ) : null}

        <button
          className="btn-primary w-full"
          onClick={handleRegisterCard}
          disabled={actionBusy || registerMutation.isPending || status === 'canceled'}
        >
          {billingKey ? '카드 다시 등록' : '결제 수단 등록'}
        </button>

        {status === 'active' && !shamanCtx?.subscription?.canceled_at && (
          <button
            className="btn-secondary w-full"
            onClick={() => {
              if (confirm('구독을 해지하시겠어요? 이번 결제 주기 끝나는 날까지 사용 가능합니다.')) {
                cancelMutation.mutate();
              }
            }}
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending ? '처리 중…' : '구독 해지'}
          </button>
        )}

        <p className="text-ink-muted text-base">
          현재 상태:{' '}
          {status === 'active'
            ? ko.subscription.statusActive
            : status === 'past_due'
              ? everPaid
                ? ko.subscription.statusPastDue
                : '결제 수단 미등록'
              : status === 'paused'
                ? ko.subscription.statusPaused
                : status === 'canceled'
                  ? ko.subscription.statusCanceled
                  : '미등록'}
        </p>
      </div>

      {!!payments?.length && (
        <div className="card space-y-3">
          <h3 className="text-lg font-bold">결제 이력</h3>
          <ul className="divide-y divide-ink-line">
            {payments.map((p) => (
              <li key={p.id} className="py-2 flex justify-between items-start gap-3">
                <div>
                  <div className="font-semibold">{formatKRW(p.amount_krw)}</div>
                  <div className="text-base text-ink-muted">
                    {formatDateTimeKo(p.attempted_at)}
                  </div>
                  {p.failure_reason && (
                    <div className="text-base text-danger">{p.failure_reason}</div>
                  )}
                </div>
                <div className={`text-base font-semibold ${p.status === 'paid' ? 'text-success' : p.status === 'failed' ? 'text-danger' : 'text-ink-muted'}`}>
                  {p.status === 'paid' ? '결제됨' : p.status === 'failed' ? '실패' : p.status}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
